'use client';

import React from 'react';
import Container from '@/ui/components/shared/Container';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import formatPunctuation from '@/ui/utils/formatPunctuation';
import { ContentChunkFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { WithdrawalOutcome } from '@/server-functions/withdrawal/withdrawal-types';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import { useWithdrawalStage } from '../withdrawal-stage';
import { WithdrawalSuccessTileType } from './types/withdrawal-success-tile-type';

type OutcomeContentKeys = {
  subline: 'cancelled-subline' | 'return-subline';
  steps: 'cancelled-steps' | 'return-steps';
};

const OUTCOME_CONTENT_KEYS: Record<WithdrawalOutcome, OutcomeContentKeys> = {
  cancelled: { subline: 'cancelled-subline', steps: 'cancelled-steps' },
  return_created: { subline: 'return-subline', steps: 'return-steps' },
};

const parseSteps = (steps?: ContentChunkFragment) =>
  (steps?.chunks ?? [])
    .map((row) => ({
      title: extractComponentFromChunk(row, 'title', 'SingleLineContent')?.text,
      text: extractComponentFromChunk(row, 'text', 'RichTextContent'),
    }))
    .filter((step) => Boolean(step.title));

// A known outcome shows its own subline and steps; without one the generic pair is shown.
const getOutcomeContent = (tile: WithdrawalSuccessTileType, outcome?: WithdrawalOutcome) => {
  if (!outcome) {
    return { subline: tile.subline, steps: parseSteps(tile.steps) };
  }

  const keys = OUTCOME_CONTENT_KEYS[outcome];

  return { subline: tile[keys.subline], steps: parseSteps(tile[keys.steps]) };
};

export function WithdrawalSuccessTile({ tile }: Readonly<{ tile: WithdrawalSuccessTileType }>) {
  const { stage, outcome } = useWithdrawalStage();

  if (stage !== 'success') {
    return null;
  }

  const { subline, steps } = getOutcomeContent(tile, outcome);

  return (
    <Container>
      <div className="mx-auto my-10 max-w-2xl space-y-6 sm:my-14">
        <div className="space-y-3 text-center">
          <h1 className="text-3xl font-bold sm:text-4xl">
            {formatPunctuation(tile.headline?.text)}
          </h1>
          <RichTextRenderer blocks={subline} className="text-md sm:text-lg" />
        </div>
        {steps.length > 0 && (
          <ol className="space-y-5">
            {steps.map((step, index) => (
              <li key={`${index}-${step.title}`} className="flex gap-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100 font-bold">
                  {index + 1}
                </span>
                <div className="space-y-1">
                  <h2 className="text-lg font-bold">{step.title}</h2>
                  <RichTextRenderer blocks={step.text} className="text-neutral-60" />
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </Container>
  );
}
